import { useId, useState } from "react";
import { Plus } from "lucide-react";
import { FEELS, MAX_NOTE } from "@/lib/results";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

/**
 * How something felt, and a note about it: the athlete's own, beside a
 * result and never part of it — a part's, in its dialog, and a teammate's
 * own on a team, on the team's page and in the log. One look everywhere
 * (Tiago's call): the feel is three toggles, one at a time — pressing the one
 * that is on turns it off — and the note waits behind "Add note" until there
 * is one. Uncontrolled like every form here: the feel goes as a hidden input,
 * empty when none is on, which is how each action reads it.
 *
 * The toggles are shadcn's toggle group, one item on at a time, which a
 * screen reader hears as radios named by `feelLabel`; the group itself sends
 * nothing, so the hidden input beside it does.
 */
export function FeelAndNote({
  feelName,
  noteName,
  feelLabel,
  feel: saved,
  note,
  form,
}: {
  /** What the form names each: a part's `parts.N.feel`, a team's own `feel`. */
  feelName: string;
  noteName: string;
  /** The toggles' name for a screen reader. */
  feelLabel: string;
  /** What is saved, which the toggles and the box start from. */
  feel: string | null;
  note: string | null;
  /** The id of the form they belong to, when it is not the one around them: a part's dialog. */
  form?: string;
}) {
  const [feel, setFeel] = useState(saved ?? "");
  const [noting, setNoting] = useState(Boolean(note));
  const noteId = useId();
  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <span aria-hidden="true" className="text-sm text-muted-foreground">
          Feel
        </span>
        <ToggleGroup
          type="single"
          variant="outline"
          aria-label={feelLabel}
          value={feel}
          // Pressing the one on turns it off: the group answers "" for none.
          onValueChange={setFeel}
        >
          {FEELS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              aria-label={option.label}
              title={option.label}
              className="px-3 text-base"
            >
              <span aria-hidden="true">{option.emoji}</span>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <input form={form} type="hidden" name={feelName} value={feel} />
      </div>
      {noting ? (
        <Field className="gap-2">
          <FieldLabel htmlFor={noteId}>
            Note <span className="font-normal text-muted-foreground">(optional)</span>
          </FieldLabel>
          <Textarea
            id={noteId}
            form={form}
            name={noteName}
            rows={2}
            maxLength={MAX_NOTE}
            defaultValue={note ?? ""}
            autoFocus={!note}
          />
        </Field>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setNoting(true)} className="self-start">
          <Plus aria-hidden="true" />
          Add note
        </Button>
      )}
    </>
  );
}
